/**
 * RichTextEditor · v2.6.9 batch CF
 * =================================
 * Tiny, zero-dependency contentEditable editor with a Microsoft-Word-style
 * toolbar. Designed so admins can format Tourism city pages, profile bios,
 * and any other long-form copy without leaving the CMS.
 *
 * Supports:
 *   • Bold / Italic / Underline / Strikethrough
 *   • Heading 1 / Heading 2 / Paragraph
 *   • Bullet + Numbered list
 *   • Alignment (left / center / right / justify)
 *   • Link insertion
 *   • Clear formatting
 *
 * Uses document.execCommand under the hood. Deprecated but still works in
 * every browser we support and avoids pulling in 200KB of Quill/TipTap
 * dependencies. Outputs sanitized-ish HTML that the public site renders
 * via dangerouslySetInnerHTML (admin-authored only).
 */
import { useRef, useEffect, useState } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link2, Eraser, Type,
} from "lucide-react";

export default function RichTextEditor({ value, onChange, label, testid = "rte", minHeight = 200 }) {
  const editorRef = useRef(null);
  const [, force] = useState(0);

  // Hydrate the editor only when the incoming value drifts from current
  // innerHTML. Avoids cursor jumping while the user is typing.
  useEffect(() => {
    if (editorRef.current && (value || "") !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(editorRef.current?.innerHTML || "");
    force((n) => n + 1);
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL (https://…)");
    if (!url) return;
    exec("createLink", url);
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  return (
    <div data-testid={`${testid}-wrap`}>
      {label && <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">{label}</div>}
      <div className="border border-zinc-700 rounded-lg overflow-hidden bg-zinc-950">
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 flex-wrap bg-zinc-900 border-b border-zinc-800 px-1.5 py-1" data-testid={`${testid}-toolbar`}>
          <ToolBtn onClick={() => exec("bold")} icon={Bold} label="Bold" testid={`${testid}-bold`}/>
          <ToolBtn onClick={() => exec("italic")} icon={Italic} label="Italic" testid={`${testid}-italic`}/>
          <ToolBtn onClick={() => exec("underline")} icon={UnderlineIcon} label="Underline" testid={`${testid}-underline`}/>
          <ToolBtn onClick={() => exec("strikeThrough")} icon={Strikethrough} label="Strikethrough" testid={`${testid}-strike`}/>
          <Divider/>
          <ToolBtn onClick={() => exec("formatBlock", "<h1>")} icon={Heading1} label="Heading 1" testid={`${testid}-h1`}/>
          <ToolBtn onClick={() => exec("formatBlock", "<h2>")} icon={Heading2} label="Heading 2" testid={`${testid}-h2`}/>
          <ToolBtn onClick={() => exec("formatBlock", "<p>")} icon={Type} label="Paragraph" testid={`${testid}-p`}/>
          <Divider/>
          <ToolBtn onClick={() => exec("insertUnorderedList")} icon={List} label="Bullet list" testid={`${testid}-ul`}/>
          <ToolBtn onClick={() => exec("insertOrderedList")} icon={ListOrdered} label="Numbered list" testid={`${testid}-ol`}/>
          <Divider/>
          <ToolBtn onClick={() => exec("justifyLeft")} icon={AlignLeft} label="Align left" testid={`${testid}-left`}/>
          <ToolBtn onClick={() => exec("justifyCenter")} icon={AlignCenter} label="Align center" testid={`${testid}-center`}/>
          <ToolBtn onClick={() => exec("justifyRight")} icon={AlignRight} label="Align right" testid={`${testid}-right`}/>
          <ToolBtn onClick={() => exec("justifyFull")} icon={AlignJustify} label="Justify" testid={`${testid}-justify`}/>
          <Divider/>
          <ToolBtn onClick={insertLink} icon={Link2} label="Insert link" testid={`${testid}-link`}/>
          <ToolBtn onClick={() => exec("removeFormat")} icon={Eraser} label="Clear formatting" testid={`${testid}-clear`}/>
        </div>
        {/* Editable canvas */}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          className="px-4 py-3 text-sm text-zinc-100 leading-relaxed focus:outline-none rich-text-editor-canvas"
          style={{ minHeight }}
          data-testid={`${testid}-canvas`}
        />
      </div>
      <div className="text-[10px] text-zinc-500 mt-1 italic">Tip: select text first, then click a toolbar button to apply formatting.</div>
    </div>
  );
}

function ToolBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      data-testid={testid}
      className="h-7 w-7 flex items-center justify-center rounded text-zinc-300 hover:bg-amber-400/20 hover:text-amber-300 transition-colors"
    >
      <Icon className="h-3.5 w-3.5"/>
    </button>
  );
}

function Divider() {
  return <span className="h-5 w-px bg-zinc-700 mx-0.5"/>;
}
